import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, 
  DialogFooter, DialogDescription 
} from '../ui/dialog';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from '../ui/select';
import { 
  ArrowLeft, TrendingUp, TrendingDown, Minus, Edit2, Plus,
  Calendar, Target, DollarSign, Users, Settings, GraduationCap,
  Save, X, ChevronDown, ChevronRight, Info, AlertTriangle,
  CheckCircle2, BarChart3, History, FileText
} from 'lucide-react';
import { bscPerspectives, mockObjectives, mockKPITrends } from '../../mock/strategyData';
import { mockCompanies } from '../../mock/data';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '../ui/tooltip';
import SectionHeader from '../shared/SectionHeader';

// Icon map
const iconMap = {
  DollarSign, Users, Settings, GraduationCap
};

// Months configuration
const MONTHS = [
  { key: 'jan', label: 'Jan', full: 'January' },
  { key: 'feb', label: 'Feb', full: 'February' },
  { key: 'mar', label: 'Mar', full: 'March' },
  { key: 'apr', label: 'Apr', full: 'April' },
  { key: 'may', label: 'May', full: 'May' },
  { key: 'jun', label: 'Jun', full: 'June' },
  { key: 'jul', label: 'Jul', full: 'July' },
  { key: 'aug', label: 'Aug', full: 'August' },
  { key: 'sep', label: 'Sep', full: 'September' },
  { key: 'oct', label: 'Oct', full: 'October' },
  { key: 'nov', label: 'Nov', full: 'November' },
  { key: 'dec', label: 'Dec', full: 'December' }
];

// Generate mock monthly data
const generateMonthlyData = (kpi) => {
  const monthlyTarget = kpi.target / 12;
  return MONTHS.map((month, index) => {
    const variance = (Math.random() - 0.5) * 0.3; // -15% to +15% variance
    const actual = index < 10 ? monthlyTarget * (1 + variance) : null; // Only 10 months have actuals
    const target = monthlyTarget;
    const achievement = actual !== null ? Math.round((actual / target) * 100) : null;
    
    return {
      month: month.key,
      label: month.label,
      full: month.full,
      target: Math.round(target * 10) / 10,
      actual: actual !== null ? Math.round(actual * 10) / 10 : null,
      achievement,
      status: achievement >= 100 ? 'exceeding' : achievement >= 90 ? 'on-track' : achievement >= 75 ? 'at-risk' : 'below-target',
      locked: index < 9 // Previous months are locked
    };
  });
};

// Sub-KPIs mock data generator
const generateSubKPIs = (parentKpi) => {
  if (!parentKpi.name.includes('Revenue') && !parentKpi.name.includes('Profit')) return [];
  
  const subKPIs = [
    { id: `${parentKpi.id}-sub1`, name: `${parentKpi.name} - Region A`, weight: 40, target: parentKpi.target * 0.4, actual: parentKpi.actual * 0.42 },
    { id: `${parentKpi.id}-sub2`, name: `${parentKpi.name} - Region B`, weight: 35, target: parentKpi.target * 0.35, actual: parentKpi.actual * 0.32 },
    { id: `${parentKpi.id}-sub3`, name: `${parentKpi.name} - Region C`, weight: 25, target: parentKpi.target * 0.25, actual: parentKpi.actual * 0.26 }
  ];
  
  return subKPIs.map(sub => ({
    ...sub,
    unit: parentKpi.unit,
    achievement: Math.round((sub.actual / sub.target) * 100),
    status: (sub.actual / sub.target) >= 1 ? 'exceeding' : (sub.actual / sub.target) >= 0.9 ? 'on-track' : 'at-risk'
  }));
};

// Status config
const statusConfig = {
  'exceeding': { label: 'Exceeding', color: 'bg-emerald-500/20 text-emerald-500 border-emerald-500/30', icon: CheckCircle2 },
  'on-track': { label: 'On Track', color: 'bg-blue-500/20 text-blue-500 border-blue-500/30', icon: CheckCircle2 },
  'at-risk': { label: 'At Risk', color: 'bg-amber-500/20 text-amber-500 border-amber-500/30', icon: AlertTriangle },
  'below-target': { label: 'Below Target', color: 'bg-orange-500/20 text-orange-500 border-orange-500/30', icon: AlertTriangle },
  'critical': { label: 'Critical', color: 'bg-red-500/20 text-red-500 border-red-500/30', icon: AlertTriangle }
};

// Monthly Data Entry Dialog
const MonthlyDataDialog = ({ open, onClose, month, data, kpi, onSave }) => {
  const [formData, setFormData] = useState({
    actual: data?.actual || '',
    target: data?.target || '',
    notes: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      month: month.key,
      actual: parseFloat(formData.actual),
      target: parseFloat(formData.target),
      notes: formData.notes
    });
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{month?.full} Data Entry</DialogTitle>
          <DialogDescription>
            Enter the actual and target values for {kpi?.name}
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Target ({kpi?.unit})</Label>
              <Input 
                type="number"
                value={formData.target}
                onChange={(e) => setFormData({ ...formData, target: e.target.value })}
                step={0.1}
                placeholder="0"
              />
            </div>
            <div>
              <Label>Actual ({kpi?.unit})</Label>
              <Input 
                type="number"
                value={formData.actual}
                onChange={(e) => setFormData({ ...formData, actual: e.target.value })}
                step={0.1}
                placeholder="0"
              />
            </div>
          </div>
          
          <div>
            <Label>Notes / Comments</Label>
            <Textarea 
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Add any relevant notes..."
              rows={2}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit">
              <Save className="w-4 h-4 mr-2" />
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Add Sub-KPI Dialog
const SubKPIDialog = ({ open, onClose, parentKpi, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    weight: 10,
    target: 0
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      id: `${parentKpi?.id}-sub-${Date.now()}`,
      ...formData,
      unit: parentKpi?.unit,
      actual: 0,
      achievement: 0,
      status: 'on-track'
    });
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Sub-KPI</DialogTitle>
          <DialogDescription>
            Create a child KPI under {parentKpi?.name}
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label>Sub-KPI Name *</Label>
            <Input 
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Revenue - North Region"
              required
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Weight (%)</Label>
              <Input 
                type="number"
                value={formData.weight}
                onChange={(e) => setFormData({ ...formData, weight: parseFloat(e.target.value) })}
                min={0}
                max={100}
                step={1}
              />
            </div>
            <div>
              <Label>Target ({parentKpi?.unit})</Label>
              <Input 
                type="number"
                value={formData.target}
                onChange={(e) => setFormData({ ...formData, target: parseFloat(e.target.value) })}
                step={0.1}
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit">Create Sub-KPI</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const KPIDetail = ({ kpiId, onBack }) => {
  const [activeTab, setActiveTab] = useState('monthly');
  const [editingMonth, setEditingMonth] = useState(null);
  const [isAddSubKPIOpen, setIsAddSubKPIOpen] = useState(false);
  const [expandedSubKPIs, setExpandedSubKPIs] = useState([]);

  // Find the KPI
  const kpi = useMemo(() => mockObjectives.find(obj => obj.id === kpiId), [kpiId]);
  const perspective = useMemo(() => bscPerspectives.find(p => p.id === kpi?.perspectiveId), [kpi]);
  const entity = useMemo(() => {
    if (!kpi) return null;
    if (kpi.entityType === 'company') return mockCompanies.find(c => c.id === kpi.entityId);
    return { name: kpi.entityType === 'enterprise' ? 'Enterprise' : kpi.entityId };
  }, [kpi]);

  // Generate monthly data
  const monthlyData = useMemo(() => kpi ? generateMonthlyData(kpi) : [], [kpi]);
  
  // Generate sub-KPIs
  const [subKPIs, setSubKPIs] = useState(() => kpi ? generateSubKPIs(kpi) : []);

  if (!kpi) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="w-12 h-12 mx-auto text-amber-500 mb-4" />
        <h3 className="text-lg font-medium text-foreground">KPI Not Found</h3>
        <p className="text-muted-foreground mt-2">The requested KPI could not be found.</p>
        <Button className="mt-4" onClick={onBack}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to KPIs
        </Button>
      </div>
    );
  }

  const achievement = kpi.target > 0 ? Math.round((kpi.actual / kpi.target) * 100) : 0;
  const TrendIcon = kpi.trend === 'up' ? TrendingUp : kpi.trend === 'down' ? TrendingDown : Minus;
  const trendColor = kpi.trend === 'up' ? 'text-emerald-500' : kpi.trend === 'down' ? 'text-red-500' : 'text-muted-foreground';
  const PerspectiveIcon = iconMap[perspective?.icon] || Target;
  const StatusIcon = statusConfig[kpi.status]?.icon || AlertTriangle;

  const handleSaveMonthlyData = (data) => {
    console.log('Saving monthly data:', data);
    // In real app, save to backend
  };

  const handleAddSubKPI = (subKPI) => {
    setSubKPIs([...subKPIs, subKPI]);
  };

  const toggleSubKPI = (id) => {
    setExpandedSubKPIs(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  // Calculate YTD
  const ytdActual = monthlyData.filter(m => m.actual !== null).reduce((sum, m) => sum + m.actual, 0);
  const ytdTarget = monthlyData.filter(m => m.actual !== null).reduce((sum, m) => sum + m.target, 0);
  const ytdAchievement = ytdTarget > 0 ? Math.round((ytdActual / ytdTarget) * 100) : 0;

  return (
    <TooltipProvider>
    <div className="space-y-6">
      {/* Breadcrumb & Actions */}
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={onBack} className="gap-2">
          <ArrowLeft className="w-4 h-4" />
          Back to KPIs
        </Button>
        <div className="flex items-center gap-2">
          <Button variant="outline">
            <History className="w-4 h-4 mr-2" />
            History
          </Button>
          <Button variant="outline">
            <FileText className="w-4 h-4 mr-2" />
            Export
          </Button>
          <Button>
            <Edit2 className="w-4 h-4 mr-2" />
            Edit KPI
          </Button>
        </div>
      </div>

      {/* KPI Header Card */}
      <SectionHeader
        customTitle={
          <>
            <div 
              className="w-8 h-8 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: `${perspective?.color}20` }}
            >
              <PerspectiveIcon className="w-4 h-4" style={{ color: perspective?.color }} />
            </div>
            <div>
              <span>{kpi.name}</span>
              <p className="text-xs font-normal text-muted-foreground">{entity?.name} • {perspective?.name}</p>
            </div>
          </>
        }
        actions={
          <Badge variant="outline" className={`${statusConfig[kpi.status]?.color}`}>
            <StatusIcon className="w-3 h-3 mr-1" />
            {statusConfig[kpi.status]?.label}
          </Badge>
        }
      >
        <div className="flex flex-col lg:flex-row gap-6">
          {/* KPI Info */}
          <div className="flex-1">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-3 rounded-lg bg-muted/30">
                <p className="text-xs text-muted-foreground mb-1">Current Actual</p>
                <p className="text-xl font-bold text-foreground">{kpi.actual}{kpi.unit}</p>
              </div>
              <div className="p-3 rounded-lg bg-muted/30">
                <p className="text-xs text-muted-foreground mb-1">Annual Target</p>
                <p className="text-xl font-bold text-foreground">{kpi.target}{kpi.unit}</p>
              </div>
              <div className="p-3 rounded-lg bg-muted/30">
                <p className="text-xs text-muted-foreground mb-1">Weight</p>
                <p className="text-xl font-bold text-foreground">{kpi.weight}%</p>
              </div>
              <div className="p-3 rounded-lg bg-muted/30">
                <p className="text-xs text-muted-foreground mb-1">Last Year</p>
                <p className="text-xl font-bold text-foreground">{kpi.lastYearActual || '-'}{kpi.lastYearActual ? kpi.unit : ''}</p>
              </div>
            </div>
          </div>

          {/* Achievement Gauge */}
          <div className="flex flex-col items-center justify-center p-6 border-l border-border/50">
            <div className="relative w-32 h-32">
              <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
                <circle cx="50" cy="50" r="45" fill="none" stroke="currentColor" strokeWidth="8" className="text-muted/20" />
                <circle 
                  cx="50" cy="50" r="45" fill="none" 
                  stroke={perspective?.color} 
                  strokeWidth="8"
                  strokeDasharray={2 * Math.PI * 45}
                  strokeDashoffset={(1 - Math.min(achievement, 100) / 100) * 2 * Math.PI * 45}
                  strokeLinecap="round"
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-3xl font-bold text-foreground">{achievement}%</span>
                <div className="flex items-center gap-1">
                  <TrendIcon className={`w-4 h-4 ${trendColor}`} />
                  <span className="text-xs text-muted-foreground">vs Target</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </SectionHeader>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="monthly" className="gap-2">
            <Calendar className="w-4 h-4" />
            Monthly Data
          </TabsTrigger>
          <TabsTrigger value="sub-kpis" className="gap-2">
            <BarChart3 className="w-4 h-4" />
            Sub-KPIs ({subKPIs.length})
          </TabsTrigger>
          <TabsTrigger value="details" className="gap-2">
            <Info className="w-4 h-4" />
            Details
          </TabsTrigger>
        </TabsList>

        {/* Monthly Data Tab */}
        <TabsContent value="monthly" className="mt-6">
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Monthly Targets & Actuals</CardTitle>
                <div className="flex items-center gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded bg-emerald-500" />
                    <span className="text-muted-foreground">YTD Actual: {ytdActual.toFixed(1)}{kpi.unit}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded border-2 border-dashed border-muted-foreground" />
                    <span className="text-muted-foreground">YTD Target: {ytdTarget.toFixed(1)}{kpi.unit}</span>
                  </div>
                  <Badge variant={ytdAchievement >= 100 ? 'default' : ytdAchievement >= 90 ? 'secondary' : 'destructive'}>
                    YTD: {ytdAchievement}%
                  </Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* Chart Visualization */}
              <div className="mb-6 h-48 flex items-end justify-between gap-2 px-4">
                {monthlyData.map((month, index) => {
                  const maxValue = Math.max(...monthlyData.map(m => Math.max(m.target, m.actual || 0)));
                  const targetHeight = (month.target / maxValue) * 100;
                  const actualHeight = month.actual !== null ? (month.actual / maxValue) * 100 : 0;
                  
                  return (
                    <div key={month.month} className="flex-1 flex flex-col items-center">
                      <div className="w-full h-40 flex items-end justify-center gap-1">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div 
                              className="w-1/3 rounded-t transition-all cursor-pointer hover:opacity-80"
                              style={{ 
                                height: `${actualHeight}%`,
                                backgroundColor: month.actual !== null 
                                  ? month.achievement >= 100 ? '#10b981' 
                                  : month.achievement >= 90 ? '#3b82f6'
                                  : month.achievement >= 75 ? '#f59e0b'
                                  : '#ef4444'
                                  : '#374151'
                              }}
                            />
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Actual: {month.actual !== null ? `${month.actual}${kpi.unit}` : 'Not entered'}</p>
                          </TooltipContent>
                        </Tooltip>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div 
                              className="w-1/3 rounded-t border-2 border-dashed border-muted-foreground/50 cursor-pointer"
                              style={{ height: `${targetHeight}%` }}
                            />
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Target: {month.target}{kpi.unit}</p>
                          </TooltipContent>
                        </Tooltip>
                      </div>
                      <span className="text-xs text-muted-foreground mt-2">{month.label}</span>
                    </div>
                  );
                })}
              </div>

              {/* Data Table */}
              <div className="border border-border rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="text-left p-3 text-xs font-medium text-muted-foreground">Month</th>
                      <th className="text-center p-3 text-xs font-medium text-muted-foreground">Target</th>
                      <th className="text-center p-3 text-xs font-medium text-muted-foreground">Actual</th>
                      <th className="text-center p-3 text-xs font-medium text-muted-foreground">Achievement</th>
                      <th className="text-center p-3 text-xs font-medium text-muted-foreground">Status</th>
                      <th className="text-center p-3 text-xs font-medium text-muted-foreground">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {monthlyData.map((month) => (
                      <tr key={month.month} className="border-t border-border/50 hover:bg-muted/20">
                        <td className="p-3 font-medium text-foreground">{month.full}</td>
                        <td className="p-3 text-center text-foreground">{month.target}{kpi.unit}</td>
                        <td className="p-3 text-center">
                          {month.actual !== null ? (
                            <span className="font-medium text-foreground">{month.actual}{kpi.unit}</span>
                          ) : (
                            <span className="text-muted-foreground italic">-</span>
                          )}
                        </td>
                        <td className="p-3 text-center">
                          {month.achievement !== null ? (
                            <span className={`font-bold ${
                              month.achievement >= 100 ? 'text-emerald-500' :
                              month.achievement >= 90 ? 'text-blue-500' :
                              month.achievement >= 75 ? 'text-amber-500' : 'text-red-500'
                            }`}>
                              {month.achievement}%
                            </span>
                          ) : '-'}
                        </td>
                        <td className="p-3 text-center">
                          {month.achievement !== null && (
                            <Badge variant="outline" className={statusConfig[month.status]?.color}>
                              {statusConfig[month.status]?.label}
                            </Badge>
                          )}
                        </td>
                        <td className="p-3 text-center">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => setEditingMonth(month)}
                            disabled={month.locked}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Sub-KPIs Tab */}
        <TabsContent value="sub-kpis" className="mt-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Sub-KPIs</CardTitle>
                <Button onClick={() => setIsAddSubKPIOpen(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Sub-KPI
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {subKPIs.length > 0 ? (
                <div className="space-y-3">
                  {subKPIs.map((sub) => (
                    <div 
                      key={sub.id}
                      className="border border-border rounded-lg overflow-hidden"
                    >
                      <div 
                        className="flex items-center justify-between p-4 cursor-pointer hover:bg-muted/20"
                        onClick={() => toggleSubKPI(sub.id)}
                      >
                        <div className="flex items-center gap-3">
                          {expandedSubKPIs.includes(sub.id) ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                          <div>
                            <h4 className="font-medium text-foreground">{sub.name}</h4>
                            <p className="text-xs text-muted-foreground">Weight: {sub.weight}%</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <p className="text-sm font-bold text-foreground">{sub.actual}{sub.unit}</p>
                            <p className="text-xs text-muted-foreground">of {sub.target}{sub.unit}</p>
                          </div>
                          <Badge variant="outline" className={statusConfig[sub.status]?.color}>
                            {sub.achievement}%
                          </Badge>
                        </div>
                      </div>
                      
                      {expandedSubKPIs.includes(sub.id) && (
                        <div className="p-4 border-t border-border/50 bg-muted/10">
                          <div className="grid grid-cols-4 gap-4 mb-4">
                            <div>
                              <p className="text-xs text-muted-foreground">Target</p>
                              <p className="font-medium text-foreground">{sub.target}{sub.unit}</p>
                            </div>
                            <div>
                              <p className="text-xs text-muted-foreground">Actual</p>
                              <p className="font-medium text-foreground">{sub.actual}{sub.unit}</p>
                            </div>
                            <div>
                              <p className="text-xs text-muted-foreground">Weight</p>
                              <p className="font-medium text-foreground">{sub.weight}%</p>
                            </div>
                            <div>
                              <p className="text-xs text-muted-foreground">Achievement</p>
                              <p className="font-bold text-foreground">{sub.achievement}%</p>
                            </div>
                          </div>
                          <Progress value={sub.achievement} className="h-2" />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <BarChart3 className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
                  <h3 className="font-medium text-foreground">No Sub-KPIs</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Break down this KPI into smaller components for better tracking
                  </p>
                  <Button className="mt-4" onClick={() => setIsAddSubKPIOpen(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Sub-KPI
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Details Tab */}
        <TabsContent value="details" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">KPI Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Description</p>
                  <p className="text-foreground">Measures the {kpi.name.toLowerCase()} performance against strategic targets</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Formula</p>
                  <p className="text-foreground font-mono text-sm bg-muted/30 p-2 rounded">(Actual / Target) × 100</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Unit of Measure</p>
                  <p className="text-foreground">{kpi.unit || 'Number'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Reporting Frequency</p>
                  <p className="text-foreground">Monthly</p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Ownership & Source</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Owner</p>
                  <p className="text-foreground">Department Head</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Data Source</p>
                  <p className="text-foreground">Finance System / ERP</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Entity</p>
                  <p className="text-foreground">{entity?.name}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Perspective</p>
                  <p className="text-foreground">{perspective?.name}</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Monthly Data Dialog */}
      <MonthlyDataDialog 
        open={!!editingMonth}
        onClose={() => setEditingMonth(null)}
        month={editingMonth}
        data={editingMonth}
        kpi={kpi}
        onSave={handleSaveMonthlyData}
      />

      {/* Add Sub-KPI Dialog */}
      <SubKPIDialog 
        open={isAddSubKPIOpen}
        onClose={() => setIsAddSubKPIOpen(false)}
        parentKpi={kpi}
        onSave={handleAddSubKPI}
      />
    </div>
    </TooltipProvider>
  );
};

export default KPIDetail;
